import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDebounce } from 'hooks'
import { AuthLayout as Layout } from '../layouts'
import { postHostBetaSignup, searchBusinesses } from 'actions/api'
import {
  Button,
  Card,
  Center,
  FormInput,
  Link,
  LocationSearch,
  Logo,
  MultiToggle,
  Title,
  Wrapper
} from 'components'
import { getAddressComponent } from 'utils'

const MODES = [
  ['Claim an existing business', 'claim'],
  ['Add a new business', 'new']
]

export const HostBetaSignup = () => {
  const { state: routerState } = useLocation()
  const [state, setState] = useState({
    email: '',
    mode: 'claim',
    query: '',
    results: [],
    location_id: routerState?.location_id || null,
    selectedName: routerState?.name || '',
    location_data: null
  })
  const [status, setStatus] = useState(null)
  const debouncedQuery = useDebounce(state.query, 300)

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setState((prev) => ({ ...prev, results: [] }))
      return
    }
    searchBusinesses(debouncedQuery).then((res) => {
      setState((prev) => ({ ...prev, results: res?.locations || [] }))
    })
  }, [debouncedQuery])

  const changeMode = ({ target: { value } }) => {
    setState((prev) => ({
      ...prev,
      mode: value[1],
      location_id: null,
      location_data: null,
      selectedName: '',
      query: '',
      results: []
    }))
  }

  const selectExisting = (location) => {
    setState((prev) => ({
      ...prev,
      location_id: location.id,
      selectedName: location.name,
      results: []
    }))
  }

  const handleNewBusiness = (address) => {
    const lat = address.geometry.location.lat()
    const lng = address.geometry.location.lng()
    setState((prev) => ({
      ...prev,
      location_id: null,
      selectedName: address.name,
      location_data: {
        name: address.name,
        addresses: [{
          ...address,
          latitude: lat,
          longitude: lng,
          coords: { type: 'Point', coordinates: [lat, lng] },
          text: address.formatted_address,
          meta: address,
          country_code: '01',
          street_address: address.formatted_address,
          city: getAddressComponent(address, 'locality'),
          country_area: getAddressComponent(address, 'administrative_area_level_1')
        }]
      }
    }))
  }

  const submit = () => {
    if (!state.email || (!state.location_id && !state.location_data)) return
    postHostBetaSignup({
      email: state.email,
      signup_type: 'host',
      location_id: state.location_id,
      location_data: state.location_data
    }).then((res) => {
      setStatus(res?.beta_signup ? 'success' : 'error')
    })
  }

  if (status === 'success') {
    return (
      <Layout>
        <Card>
          <Wrapper margin="4rem 0">
            <Center>
              <Logo width="250" height="250" />
            </Center>
          </Wrapper>
          <Wrapper margin="0.5rem">
            <Title level={3} color="link">You're on the list!</Title>
          </Wrapper>
          <Wrapper margin="0.5rem">
            We'll email you an invite once we've reviewed your business.
          </Wrapper>
        </Card>
      </Layout>
    )
  }

  return (
    <Layout>
      <Card>
        <Wrapper margin="4rem 0">
          <Center>
            <Logo width="250" height="250" />
          </Center>
        </Wrapper>
        <Wrapper margin="0.5rem">
          <Title level={3} color="link">Bring your business to Pinmonkey</Title>
        </Wrapper>
        <Wrapper margin="0.5rem">
          Tell us who you are and which business is yours. We'll email you an invite once it's reviewed.
        </Wrapper>
        {status === 'error' && (
          <Wrapper margin="0.5rem">
            Something went wrong. Please try again.
          </Wrapper>
        )}
        <FormInput
          label="Email"
          name="email"
          onChange={({ target: { value } }) => setState((prev) => ({ ...prev, email: value }))}
          value={state.email}
        />
        <Wrapper margin="0.5rem">
          <MultiToggle data={MODES} step={state.mode} onChange={changeMode} />
        </Wrapper>
        {state.mode === 'claim' && (
          <Wrapper margin="0.5rem">
            <FormInput
              label="Search your business"
              name="query"
              onChange={({ target: { value } }) => setState((prev) => ({ ...prev, query: value }))}
              value={state.query}
            />
            {state.results.map((location) => (
              <Button key={location.id} onPress={() => selectExisting(location)}>
                {location.name}
              </Button>
            ))}
          </Wrapper>
        )}
        {state.mode === 'new' && (
          <Wrapper margin="0.5rem">
            <LocationSearch onSelect={handleNewBusiness} />
          </Wrapper>
        )}
        {state.selectedName && (
          <Wrapper margin="0.5rem">
            Selected: {state.selectedName}
          </Wrapper>
        )}
        <Button className="large" onPress={submit}>
          Join waitlist
        </Button>
        <Wrapper margin="0.5rem">
          <Link to="/beta">Just looking for yourself? Join the regular waitlist</Link>
        </Wrapper>
      </Card>
    </Layout>
  )
}
